import { useMultiPressReveal } from '@griply/hooks';
import { Locales } from '@griply/types';
import { CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import React, { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'shared/assets/icons';
import { FormSelect } from 'shared/components/ui/form-select';
import { IconButton } from 'shared/components/ui/icon-button';
import { useDateFormatOptions } from 'shared/hooks/use-date-format-options';
import { useNumberFormatOptions } from 'shared/hooks/use-number-format-options';
import { useSupportedLanguageOptions } from 'shared/hooks/use-supported-language-options';
import { useThemeModeOptions } from 'shared/hooks/use-theme-mode-options';
import { useTimeFormatOptions } from 'shared/hooks/use-time-format-options';
import { useWeekDaysOptions } from 'shared/hooks/use-week-days-options';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { NumberFormatOptions } from 'shared/types/number-format-options';
import { ThemeModes } from 'shared/types/theme-modes';
import { WeekDays } from 'shared/types/week-days';

import { AccountSettings } from './account-settings';
import { ExternalCalendarList } from './external-calendar-list';
import { NotificationsDebugPanel } from './notifications-debug-panel';
import { NotificationsToggles } from './notifications-toggles';
import { PaymentManagement } from './payment-management';
import { QuickEntrySettings } from './quick-entry-settings';
import * as Styled from './settings-form.style';
import { settingsNavItems, SettingsSection } from './settings-nav';
import { SettingsNavigation } from './settings-navigation';

const DEBUG_PANEL_CLICK_COUNT = 3;
const DEBUG_PANEL_CLICK_RESET_MS = 1500;

export type SettingsFormProps = {
  onClose: () => void;
  name: string;
  onChangeName: (name: string) => void;
  language?: Locales;
  onChangeLanguage: (language: Locales) => void;
  isSignedInWithProvider?: boolean;
  email: string;
  onChangePassword: () => void;
  numberFormat?: NumberFormatOptions;
  onChangeNumberFormat: (numberFormat: NumberFormatOptions) => void;
  dateFormat: DateFormatOptions;
  onChangeDateFormat: (dateFormat: DateFormatOptions) => void;
  timeFormat: TimeFormatOptions;
  onChangeTimeFormat: (dateFormat: TimeFormatOptions) => void;
  weekStartsOn: WeekDays;
  onChangeWeekStart: (weekDay: WeekDays) => void;
  themePreference: ThemeModes;
  onChangeThemePreference: (themeMode: ThemeModes) => void;
};

export const SettingsForm: React.FC<SettingsFormProps> = ({
  onClose,
  name,
  onChangeName,
  language,
  onChangeLanguage,
  isSignedInWithProvider,
  email,
  onChangePassword,
  numberFormat,
  onChangeNumberFormat,
  dateFormat,
  onChangeDateFormat,
  timeFormat,
  onChangeTimeFormat,
  weekStartsOn,
  onChangeWeekStart,
  themePreference,
  onChangeThemePreference,
}) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState(SettingsSection.Account);
  const supportedLanguageOptions = useSupportedLanguageOptions();
  const numberFormatOptions = useNumberFormatOptions();
  const dateFormatOptions = useDateFormatOptions();
  const timeFormatOptions = useTimeFormatOptions();
  const themeModeOptions = useThemeModeOptions();
  const weekDaysOptions = useWeekDaysOptions();
  const { isRevealed: isDebugVisible, onPress: onNotificationsLabelClick } =
    useMultiPressReveal({
      pressCount: DEBUG_PANEL_CLICK_COUNT,
      resetMs: DEBUG_PANEL_CLICK_RESET_MS,
    });

  const activeNavItem = settingsNavItems.find(
    (item) => item.section === activeSection,
  );

  const onChangeLang = (e: React.ChangeEvent<HTMLSelectElement>) =>
    onChangeLanguage(e.target.value as Locales);

  const onChangeNumberFormatOption = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => onChangeNumberFormat(e.target.value as NumberFormatOptions);

  const onChangeDateFormatOption = (e: React.ChangeEvent<HTMLSelectElement>) =>
    onChangeDateFormat(e.target.value as DateFormatOptions);

  const onChangeTimeFormatOption = (e: React.ChangeEvent<HTMLSelectElement>) =>
    onChangeTimeFormat(e.target.value as TimeFormatOptions);

  const onChangeWeekStartsOnOption = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => onChangeWeekStart(e.target.value as WeekDays);

  const onChangeThemeMode = (e: React.ChangeEvent<HTMLSelectElement>) =>
    onChangeThemePreference(e.target.value as ThemeModes);

  const onTitleClick =
    activeSection === SettingsSection.Notifications
      ? onNotificationsLabelClick
      : undefined;

  return (
    <Styled.Panel>
      <SettingsNavigation
        activeSection={activeSection}
        onSelect={setActiveSection}
      />

      <Styled.Content>
        <Styled.ContentHeader>
          <Typography variant="h5" component="h2" onClick={onTitleClick}>
            {!!activeNavItem && t(activeNavItem.translationLabel)}
          </Typography>
          <IconButton icon={X} onClick={onClose} />
        </Styled.ContentHeader>

        <Styled.ContentBody>
          {activeSection === SettingsSection.Account && (
            <AccountSettings
              name={name}
              onChangeName={onChangeName}
              email={email}
              isSignedInWithProvider={isSignedInWithProvider}
              onChangePassword={onChangePassword}
            />
          )}

          {activeSection === SettingsSection.Subscription && (
            <Suspense
              fallback={<CircularProgress size="3.2rem" color="info" />}
            >
              <PaymentManagement />
            </Suspense>
          )}

          {activeSection === SettingsSection.QuickEntry && (
            <QuickEntrySettings />
          )}

          {activeSection === SettingsSection.Notifications && (
            <>
              <NotificationsToggles />
              {isDebugVisible && (
                <Suspense
                  fallback={<CircularProgress size="3.2rem" color="info" />}
                >
                  <NotificationsDebugPanel />
                </Suspense>
              )}
            </>
          )}

          {activeSection === SettingsSection.Theme && (
            <Styled.Section>
              <FormSelect
                id="thema"
                options={themeModeOptions}
                label={t(
                  'forms.user-settings.sections.appearance.theme-preference.label',
                )}
                value={themePreference}
                onChange={onChangeThemeMode}
                fullWidth
              />
            </Styled.Section>
          )}

          {activeSection === SettingsSection.DateTime && (
            <Styled.Section>
              <FormSelect
                id="date-format"
                options={dateFormatOptions}
                label={t(
                  'forms.user-settings.sections.date-and-time.date-format.label',
                )}
                value={dateFormat}
                onChange={onChangeDateFormatOption}
                fullWidth
              />
              <FormSelect
                id="time-format"
                options={timeFormatOptions}
                label={t(
                  'forms.user-settings.sections.date-and-time.time-format.label',
                )}
                value={timeFormat}
                onChange={onChangeTimeFormatOption}
                fullWidth
              />
              <FormSelect
                id="week-start"
                options={weekDaysOptions}
                label={t(
                  'forms.user-settings.sections.date-and-time.week-start.label',
                )}
                value={weekStartsOn}
                onChange={onChangeWeekStartsOnOption}
                fullWidth
              />
            </Styled.Section>
          )}

          {activeSection === SettingsSection.Language && (
            <Styled.Section>
              <FormSelect
                id="language"
                options={supportedLanguageOptions}
                placeholder={t(
                  'forms.user-settings.sections.language.fields.language.placeholder',
                )}
                label={t(
                  'forms.user-settings.sections.language.fields.language.label',
                )}
                value={language}
                onChange={onChangeLang}
                fullWidth
              />
              <FormSelect
                id="number-format"
                options={numberFormatOptions}
                label={t('forms.user-settings.number-format.label')}
                value={numberFormat}
                onChange={onChangeNumberFormatOption}
                fullWidth
              />
            </Styled.Section>
          )}

          {activeSection === SettingsSection.Calendar && (
            <Suspense
              fallback={<CircularProgress size="3.2rem" color="info" />}
            >
              <ExternalCalendarList />
            </Suspense>
          )}
        </Styled.ContentBody>
      </Styled.Content>
    </Styled.Panel>
  );
};
